import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useMountedState, usePromise } from "react-use";
import { proxy, useSnapshot } from "valtio";

import classNames from "classnames";
import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useCart } from "../../hooks/useCart";
import { useDefinition } from "../../hooks/useDefinition";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useLabels } from "../../hooks/useLabels";
import { useNavigateToUrl } from "../../hooks/useNavigate";
import { usePrice } from "../../hooks/usePrice";
import { useResizeObserver } from "../../hooks/useResizeObserver";

import { waitForPendingUploadsAsync } from "../dialogs/pending-uploads-dialog";
import { showPriceDialog } from "../dialogs/price-dialog";

import { CtaButton } from "../cta-button";
import { Icon, IconName } from "../icon";
import { Logo } from "../logo";
import { showProductValidationDialogAsync } from "../product-validation-dialog";
import { TransparentButton } from "../transparent-button";

import { useDashboardContext } from "../../contexts/dashboard-context";
import { usePreviewContext } from "../../contexts/preview-context";
import { useMessage } from "../../hooks/useMessage";
import { Header } from "./header";
import { labels } from "./labels";
import { IconButton } from "../icon-button";

const headerState = proxy({
	isLargeLogo: true,
	isLargeButton: true,
});

const HeaderPreviewLeft = () => {
	const googleAnalytics = useGoogleAnalytics();
	const [, setIsPreviewMode] = useIsPreviewMode();

	return (
		<div className="header__left__wrapper">
			<div>
				<IconButton
					iconName="angle-left-slim"
					onClick={() => {
						googleAnalytics.trackEventToGa({
							trackingAction: "PreviewClosed",
						});
						setIsPreviewMode(false);
					}}
				/>
			</div>
		</div>
	);
};

const HeaderDefaultLeft = () => {
	const { editor, context } = useAppContext();

	const dashboard = useDashboardContext();

	const googleAnalytics = useGoogleAnalytics();
	const definitionForRender = useDefinition();
	const navigateToUrl = useNavigateToUrl();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();
	const cart = useCart();

	const [clearDesignFilters, setClearDesignFilters] = useState(false);

	const hasDspUrl = !!(definitionForRender && definitionForRender.product?.designSelectionLink);

	useMessage((message) => {
		if (
			!eXpress.core.isFirstDefinitionLoad(message) &&
			eXpress.core.isProductCodeChanged(message)
		) {
			setClearDesignFilters(true);
		}
	});

	const onBackClick = useCallback(() => {
		const url =
			cart && hasDspUrl
				? dashboard.getDesignSelectionLink(
						cart,
						editor,
						clearDesignFilters,
						context.cartItemId,
				  ) // TODO: refactor to use lazy loaded shopping cart (make async)
				: (definitionForRender &&
						dashboard.getProductUrlLink(definitionForRender.product)) ||
				  "";

		googleAnalytics.trackEventToGa({
			trackingAction: hasDspUrl ? "BackToDesigns" : "BackToProduct",
		});

		busyBlocker
			.during(editor.saveDefinitionAsActiveAsync())
			.then(() => navigateToUrl(url, { backButtonClicked: true }))
			.catch(createErrorHandler());
	}, [
		cart,
		context,
		dashboard,
		hasDspUrl,
		editor,
		clearDesignFilters,
		googleAnalytics,
		busyBlocker,
		definitionForRender,
		createErrorHandler,
		navigateToUrl,
	]);

	const containerRef = useRef<HTMLDivElement>(null);
	const backButtonRef = useRef<HTMLDivElement>(null);

	const headerStateSnapshot = useSnapshot(headerState);

	const checkLogoSize = () => {
		if (!containerRef.current || !backButtonRef.current) return;

		const wrapperWidth = containerRef.current.clientWidth;
		const backButtonWidth = backButtonRef.current.offsetWidth;

		headerState.isLargeLogo = headerState.isLargeButton
			? wrapperWidth > 140 + backButtonWidth
			: false;
	};

	useResizeObserver(containerRef, checkLogoSize);

	const styles = classNames("header__left__wrapper", {
		"header__left__wrapper--small": !headerState.isLargeLogo,
	});

	return (
		<div ref={containerRef} className={styles}>
			<div ref={backButtonRef}>
				<IconButton iconName="angle-left-slim" onClick={onBackClick} />
			</div>
			<Logo showLargeLogo={headerStateSnapshot.isLargeLogo} />
		</div>
	);
};

const HeaderRight = ({ updateFlow }: { updateFlow?: boolean }) => {
	const {
		editor,
		getFontSizeCorrectorAsync,
		googleAnalytics,
		platform: { queryString },
	} = useAppContext();

	const shouldValidate = useRef(true);
	const [isPreviewMode, setIsPreviewMode] = useIsPreviewMode();
	const definition = useDefinition();
	const isPreviewRequired = definition?.product?.isPreviewRequiredMC;

	let ctaButtonLabel = labels.addToCart;
	if (updateFlow) ctaButtonLabel = labels.updateCart;
	if (isPreviewRequired && !isPreviewMode) ctaButtonLabel = labels.next;

	const [[ctaButtonText], areLabelsLoading] = useLabels([ctaButtonLabel]);

	const ctaButtonIconName: IconName =
		isPreviewRequired && !isPreviewMode ? "angle-right" : "shopping-cart-plus";

	const shoppingCart = useCart();
	const { busyBlocker } = useBusyBlockerContext();

	const navigateToUrl = useNavigateToUrl();

	const resolveIfMounted = usePromise();
	const isMounted = useMountedState();

	const price = usePrice();

	const { createErrorHandler } = useErrorHandling();

	const { goTo } = usePreviewContext();

	const [isAddToBasketBusy, setIsAddToBasketBusy] = useState(false);

	useEffect(() => {
		if (!isPreviewMode) shouldValidate.current = true;
	}, [isPreviewMode]);

	/**
	 * Validate Definition and show message if needed
	 * @return true if valid (or warnings are ingored), false if definition has problems
	 */
	const validateDefinitionAsync = useCallback(async () => {
		const messages = await resolveIfMounted(
			// Don't usePromise inside a BalanacedScope (like busyBlocker)!
			busyBlocker.while(
				editor.validateAsync({
					getFontSizeCorrectorAsync,
					getLocalizedMonthNames: undefined,
				}),
			),
		);

		const fixableByUser = messages.some((msg) => {
			if (msg.name === "TextOutsideBounds") {
				return msg.details.affectedItems.some(({ canBeFixedByUser }) => canBeFixedByUser);
			}
			return true;
		});
		return messages.length && fixableByUser
			? showProductValidationDialogAsync(messages, isPreviewRequired ? "preview" : "cart")
			: true;
	}, [busyBlocker, editor, getFontSizeCorrectorAsync, isPreviewRequired, resolveIfMounted]);

	const onAddToBasketClick = useCallback(async () => {
		// Log when clicked
		googleAnalytics.trackEventToGa({
			trackingAction: isPreviewMode ? "AddToCartFromPreview" : "AddToCart",
		});

		if (!definition || !shoppingCart) return;

		// Update Cart and navigate away
		const updateCartAsync = () =>
			busyBlocker.while(
				editor.updateShoppingCartAsync().then((result) => {
					const firstAffectedCartItemId = result.cartItemIds?.first();
					if (eXpress.GuidString.isNonEmpty(firstAffectedCartItemId)) {
						window.history.replaceState(
							null,
							"",
							queryString.addOrReplaceQueryStringParameterInUrl(
								window.location.href,
								"cartId",
								firstAffectedCartItemId,
							),
						);
					}

					return result;
				}),
			);

		setIsAddToBasketBusy(true); // prevent button can be clicked again when busy

		try {
			const hasFinishedUploading = await waitForPendingUploadsAsync({
				getHasPendingUploads: () => editor.hasPendingUploads(true),
			});

			if (!hasFinishedUploading) return;

			// validation may have already happened in the preview (for mandatory preview products)
			if (shouldValidate.current) {
				const canContinue = await validateDefinitionAsync();
				shouldValidate.current = !canContinue;
				if (!canContinue) return;
			}

			const { redirectUrl } = await updateCartAsync();

			// on iPhone when navigating away and pressing back,
			// the page will be shown like it was left (no refresh)
			// Therefor we enable the busy button before navigating away
			if (isMounted()) setIsAddToBasketBusy(false);

			navigateToUrl(redirectUrl, {
				waitForPendingUploads: "onlyUsed",
			});
		} catch (error) {
			createErrorHandler()(error);
		} finally {
			if (isMounted()) setIsAddToBasketBusy(false);
		}
	}, [
		googleAnalytics,
		isPreviewMode,
		createErrorHandler,
		editor,
		shoppingCart,
		busyBlocker,
		navigateToUrl,
		isMounted,
		queryString,
		definition,
		validateDefinitionAsync,
	]);

	const onNextClick = useCallback(async () => {
		const canContinue = await validateDefinitionAsync();
		shouldValidate.current = !canContinue;
		if (!canContinue) return;

		const defaultSpreadIndex = definition?.getDefaultSpreadIndex() ?? 0;
		goTo(defaultSpreadIndex);
		setIsPreviewMode(true);
	}, [definition, goTo, setIsPreviewMode, validateDefinitionAsync]);

	const isAddToBasketDisabled = isAddToBasketBusy || !shoppingCart;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const priceRef = useRef<HTMLDivElement>(null);
	const ctaTextButtonRef = useRef<HTMLButtonElement>(null);

	const headerStateSnapshot = useSnapshot(headerState);

	// When available width is larger than button width, set showLongCta boolean
	const checkSize = useCallback(() => {
		if (!ctaTextButtonRef.current || !wrapperRef.current || !priceRef.current) return;

		const { offsetWidth: textButtonWidth } = ctaTextButtonRef.current;
		const { offsetWidth: priceWidth } = priceRef.current;
		const { clientWidth: wrapperWidth } = wrapperRef.current;

		headerState.isLargeButton =
			headerState.isLargeLogo || wrapperWidth > textButtonWidth + priceWidth;
	}, []);

	useResizeObserver(wrapperRef, checkSize, areLabelsLoading);

	useEffect(() => {
		if (!price) return;

		checkSize();
	}, [price, checkSize]);

	if (areLabelsLoading) return null;
	return (
		<div ref={wrapperRef} className="header__right__wrapper">
			{!isPreviewMode && (
				<div ref={priceRef}>
					<TransparentButton
						className="header__price"
						onClick={() => {
							if (price) {
								googleAnalytics.trackEventToGa({
									trackingAction: "PriceClicked",
								});
								// eslint-disable-next-line @typescript-eslint/no-floating-promises
								showPriceDialog();
							}
						}}
						role="button"
					>
						{price ? price.toString() : ""}
					</TransparentButton>
				</div>
			)}
			<CtaButton
				onClick={() => {
					if (isAddToBasketDisabled) return;
					if (!isPreviewRequired || isPreviewMode) {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						onAddToBasketClick();
					} else {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						onNextClick();
					}
				}}
				disabled={false}
				ref={ctaTextButtonRef}
				hidden={!headerStateSnapshot.isLargeButton}
			>
				{ctaButtonText}
			</CtaButton>
			<CtaButton
				onClick={() => {
					if (isAddToBasketDisabled) return;
					if (!isPreviewRequired || isPreviewMode) {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						onAddToBasketClick();
					} else {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						onNextClick();
					}
				}}
				disabled={false}
				hidden={headerStateSnapshot.isLargeButton}
			>
				<Icon iconName={ctaButtonIconName} />
			</CtaButton>
		</div>
	);
};

export const HeaderContainer = memo(function HeaderContainer() {
	const [isPreviewMode] = useIsPreviewMode();
	const { context } = useAppContext();

	const isUpdateFlow = !!eXpress.GuidString.isNonEmpty(context.cartItemId);
	return (
		<Header
			left={isPreviewMode ? <HeaderPreviewLeft /> : <HeaderDefaultLeft />}
			right={<HeaderRight updateFlow={isUpdateFlow} />}
		/>
	);
});
